import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import ShoppingBag from "@insite/mobius/Icons/ShoppingBag";
import ShoppingCart from "@insite/mobius/Icons/ShoppingCart";
import Truck from "@insite/mobius/Icons/Truck";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, withTheme } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
}

const mapStateToProps = (state: ApplicationState) => ({
    shouldLoadCart: !getCurrentCartState(state).value && !getCurrentCartState(state).isLoading,
    totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay,
    cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
    cart: getCurrentCartState(state).value,
});

const mapDispatchToProps = {
    loadCurrentCart,
};

export interface CartLinkStyles {
    routerLink?: LinkPresentationProps;
    desktopCountText?: TypographyPresentationProps;
    mobileCountText?: TypographyPresentationProps;
    compactCountText?: TypographyPresentationProps;
    mainContainer?: InjectableCss;
    popoverContainer?: InjectableCss;
    headerContainer?: InjectableCss;
    popoverWrapper?: InjectableCss;
    noOrderLine?: InjectableCss;
    cartLine?: InjectableCss;
    cartLineInfoItem?: InjectableCss;
    qtyDescriptionWrapper?: InjectableCss;
    priceContainer?: InjectableCss;
    subtotalWrapper?: InjectableCss;
    qtyOrdered?: TypographyPresentationProps;
    unitListPrice?: TypographyPresentationProps;
    shortDescription?: TypographyPresentationProps;
    subtotalText?: TypographyPresentationProps;
    subtotalAmount?: TypographyPresentationProps;
    linksWrapper?: InjectableCss;
}

export const cartLinkStyles: CartLinkStyles = {
    routerLink: {
        typographyProps: {
            variant: "headerSecondary",
        },
        icon: {
            iconProps: {
                size: 24,
            },
        },
        color: "secondary.contrast",
        css: css`
            height: 35px;
            padding: 0 4px 0 10px;
        `,
    },
    compactCountText: {
        css: css`
            font-weight: normal;
        `,
    },
    mainContainer: {
        css: css`
            position: relative;
        `,
    },
    popoverContainer: {
        css: css`
            position: absolute;
            top: 32px;
            right: 0;
            background-color: #f7f7f7 !important;
            box-shadow: 0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14),
                0px 1px 10px 0px rgba(0, 0, 0, 0.12);
            z-index: 1290;
            width: 525px;
            border: 2px solid ${getColor("common.border")};
            border-radius: 2px;
        `,
    },
    headerContainer: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            background-color: #f7f7f7 !important;
        `,
    },
    popoverWrapper: {
        css: css`
            background-color: #f7f7f7 !important;
            display: flex;
            flex-direction: column;
            gap: 3px;
            padding-top: 5px;
            padding-left: 10px;
            padding-right: 3px;
            max-height: 350px;
            overflow-y: auto;
            span {
                font-size: 14px;
                font-weight: normal;
                color: #444444 !important;
            }
            &::-webkit-scrollbar {
                width: 8px;
            }

            &::-webkit-scrollbar-track {
                background: #f1f1f1;
                border-radius: 5px;
            }

            &::-webkit-scrollbar-thumb {
                background: #8888889c;
                border-radius: 5px;
            }

            &::-webkit-scrollbar-thumb:hover {
                background: #888888dc;
            }
        `,
    },
    noOrderLine: {
        css: css`
            font-size: 18px !important;
            font-weight: bold !important;
            color: black !important;
            margin: 20px;
        `,
    },
    cartLine: {
        css: css`
            display: flex;
        `,
    },
    cartLineInfoItem: {
        css: css`
            background-color: #f7f7f7 !important;
            flex-grow: 1;
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
        `,
    },
    qtyDescriptionWrapper: {
        css: css`
            background-color: #f7f7f7 !important;
            &:hover .description {
                color: #b00 !important;
            }
            cursor: pointer;
            padding-right: 20px;
        `,
    },
    priceContainer: {
        css: css`
            display: flex;
            justify-content: space-between;
            background-color: #f7f7f7 !important;
        `,
    },
    subtotalWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            border-bottom: 1px solid #444444;
            padding: 5px 3px 10px 10px;
        `,
    },
    qtyOrdered: {
        css: css`
            margin-bottom: 0;
            font-size: 15px;
            font-weight: normal;
            color: #444444 !important;
            margin-right: 5px;
        `,
    },
    unitListPrice: {
        css: css`
            margin-bottom: 0;
            font-size: 15px;
            font-weight: normal;
            margin-left: 10px;
        `,
    },
    shortDescription: {
        size: 14,
    },
    subtotalText: {
        css: css`
            margin-bottom: 0;
            font-size: 16px;
            font-weight: bold;
            color: black !important;
        `,
    },
    subtotalAmount: {
        variant: "p",
        css: css`
            margin-bottom: 0;
            font-size: 16px;
            font-weight: bold;
            color: black !important;
        `,
    },
    linksWrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            background-color: #f7f7f7 !important;
            padding: 6px 10px;
            font-size: 15px;
        `,
    },
};

const iconMap = {
    cart: ShoppingCart,
    truck: Truck,
    bag: ShoppingBag,
    none: undefined,
} as const;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: "both" | "label" | "icon";
        [fields.icon]: keyof typeof iconMap;
    };
    isCompactHeaderCart?: boolean;
    theme: BaseTheme;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

/** Reloading the cart makes `totalCountDisplay` temporarily unavailable, so retain the previous value for better UX. */
let retainedTotalCountToDisplay: number | undefined;

const styles = cartLinkStyles;
const CartLink: FC<Props> = ({
    shouldLoadCart,
    cartUrl,
    totalCountDisplay,
    fields: { visibilityState, icon: selectedIcon },
    loadCurrentCart,
    isCompactHeaderCart,
    theme,
    cart,
}) => {
    const cartLines = cart?.cartLines;
    const history = useHistory();
    const [showPopover, setShowPopover] = useState(false);

    useEffect(() => {
        if (shouldLoadCart) {
            loadCurrentCart({ replaceCart: false, getCartLines: true });
        }
    }, [shouldLoadCart]);

    const styles = useMergeStyles("cartLink", cartLinkStyles);

    const showIcon = visibilityState !== "label";
    const showLabel = visibilityState !== "icon";

    if (typeof totalCountDisplay !== "undefined") {
        retainedTotalCountToDisplay = totalCountDisplay;
    }

    const icon = iconMap[(showIcon && selectedIcon) || "none"];

    const { routerLink } = styles;

    if (isCompactHeaderCart) {
        return (
            <Link
                {...routerLink}
                color={theme.header.linkColor}
                href={cartUrl}
                icon={{ ...routerLink?.icon, iconProps: { src: icon, ...routerLink?.icon?.iconProps } }}
                data-test-selector="cartLink"
            >
                <Typography as="span" {...styles.compactCountText}>
                    {retainedTotalCountToDisplay}
                </Typography>
                <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
            </Link>
        );
    }

    const handlePopoverDisplay = (value: boolean) => {
        if (window?.innerWidth > 1024) {
            setShowPopover(value);
        }
    };

    const handleLinkClick = () => {
        setShowPopover(false);
    };

    return (
        <StyledWrapper
            {...styles.mainContainer}
            onMouseOver={() => handlePopoverDisplay(true)}
            onMouseOut={() => handlePopoverDisplay(false)}
        >
            <Link
                {...routerLink}
                href={cartUrl}
                onClick={handleLinkClick}
                icon={{ ...routerLink?.icon, iconProps: { src: icon, ...routerLink?.icon?.iconProps } }}
                data-test-selector="cartLink"
            >
                <Hidden below="lg" as="span">
                    {showLabel && `${translate("Cart")} `}
                    <Typography as="span" className="Paranthisis">
                        (
                    </Typography>
                    <span data-test-selector="cartLinkQuantity">{retainedTotalCountToDisplay}</span>
                    <Typography as="span" className="Paranthisis">
                        )
                    </Typography>
                </Hidden>
                <Hidden above="md" as="span">
                    <Typography as="span" className="Paranthisis">
                        (
                    </Typography>
                    <span data-test-selector="cartLinkQuantity">{retainedTotalCountToDisplay}</span>
                    <Typography as="span" className="Paranthisis">
                        )
                    </Typography>
                </Hidden>
                <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
            </Link>
            {/* cart popover */}
            {showPopover && (
                <StyledWrapper {...styles.popoverContainer}>
                    <StyledWrapper {...styles.popoverWrapper}>
                        {cartLines && cartLines.length === 0 ? (
                            <Typography {...styles.noOrderLine}>{siteMessage("Cart_NoOrderLines")}</Typography>
                        ) : (
                            cartLines?.map((item: CartLineModel) => {
                                return (
                                    <StyledWrapper key={item.id} {...styles.cartLine}>
                                        <StyledWrapper {...styles.cartLineInfoItem}>
                                            <StyledWrapper
                                                {...styles.qtyDescriptionWrapper}
                                                onClick={() => item?.productUri && history.push(item.productUri)}
                                            >
                                                <Typography
                                                    className="description"
                                                    {...styles.qtyOrdered}
                                                >{`(${item?.qtyOrdered})`}</Typography>
                                                <Typography className="description" {...styles.shortDescription}>
                                                    {item?.shortDescription}
                                                </Typography>
                                            </StyledWrapper>
                                            <StyledWrapper {...styles.priceContainer}>
                                                <Typography {...styles.unitListPrice}>
                                                    {item?.pricing?.extendedUnitNetPriceDisplay}
                                                </Typography>
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                );
                            })
                        )}
                    </StyledWrapper>
                    {cartLines && cartLines.length > 0 && (
                        <>
                            <StyledWrapper {...styles.subtotalWrapper}>
                                <Typography {...styles.subtotalText}>{translate("Total")}</Typography>
                                <Typography {...styles.subtotalAmount}>{cart?.orderSubTotalDisplay}</Typography>
                            </StyledWrapper>
                            <StyledWrapper {...styles.linksWrapper}>
                                <Link href="/Cart" onClick={handleLinkClick}>
                                    {translate("View Shopping Cart")}
                                </Link>
                                <Link href="/Rfq" onClick={handleLinkClick}>
                                    {translate("Request a Quote")}
                                </Link>
                            </StyledWrapper>
                        </>
                    )}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(CartLink)),
    definition: {
        displayName: "Cart",
        icon: "cart-shopping",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Show Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label Only",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon Only",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "Shopping Cart Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "None",
                        value: "none",
                    },
                    {
                        displayName: "Bag",
                        value: "bag",
                    },
                    {
                        displayName: "Cart",
                        value: "cart",
                    },
                    {
                        displayName: "Truck",
                        value: "truck",
                    },
                ],
                defaultValue: "cart",
                fieldType: "General",
                sortOrder: 2,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
